import React from 'react';
import { Modal as MModal, ModalBody, ModalContent } from "@heroui/react";

import { store, useAppSelector } from '@/app/store';

import Button from '@/components/Common/Button';
import FlexRow from '@/components/Common/FlexRow';

// Renders when the backend pushes a `session_expiring` event over the
// WebSocket (spec Feature-111). The state is held in
// `state.websocket.sessionExpiring`; we read it here and self-mount.
// Two actions:
//   - Stay logged in → GET /login.json refreshes the server's LastSeen
//   - Log out now    → store.performLogout()
// When the countdown reaches 0 we auto-fire the logout (spec §6).

function formatMMSS(seconds: number): string {
  const safe = Math.max(0, seconds);
  const m = Math.floor(safe / 60);
  const s = safe % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

const SessionExpiringModal: React.FC = () => {
  const expiring = useAppSelector((state) => state.websocket.sessionExpiring);
  const [now, setNow] = React.useState(() => Date.now());
  const [staying, setStaying] = React.useState(false);
  const [retry, setRetry] = React.useState(false);

  // Tick once per second while the modal is mounted.
  React.useEffect(() => {
    if (!expiring) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [expiring]);

  const remaining = React.useMemo(() => {
    if (!expiring) return 0;
    const deadline = expiring.receivedAt + expiring.secondsUntilLogout * 1000;
    return Math.ceil((deadline - now) / 1000);
  }, [expiring, now]);

  // Auto-logout the moment the countdown hits zero. Spec §6: don't wait
  // for the user.
  React.useEffect(() => {
    if (!expiring) return;
    if (remaining > 0) return;
    store.performLogout();
  }, [expiring, remaining]);

  if (!expiring) return null;

  const handleStay = async () => {
    setRetry(false);
    setStaying(true);
    try {
      const ok = await store.refreshSession();
      if (ok) {
        // clearSessionExpiring already dispatched inside refreshSession.
        return;
      }
      // 401 or network — backend may have evicted us already. Per spec §5,
      // fall through to logout in that case; otherwise keep modal open and
      // offer a retry.
      if (remaining <= 0) {
        store.performLogout();
      } else {
        setRetry(true);
      }
    } finally {
      setStaying(false);
    }
  };

  const handleLogout = () => {
    store.clearSessionExpiring();
    store.performLogout();
  };

  return (
    <MModal backdrop="opaque"
            defaultOpen={true}
            hideCloseButton={true}
            isDismissable={false}
            classNames={{
              backdrop: "bg-[#0000007f]",
              wrapper: "modalShadow",
            }}
            radius="none">
      <ModalContent>
        <ModalBody className="min-w-[420px] p-[24px]">
          <FlexRow direction="column" className="gap-[14px]">
            <FlexRow flex={0} className="gap-[10px] items-center">
              <span className="material-icons !text-[--Warning] text-[22px]">schedule</span>
              <h2 className="m14 text-black font-bold m-0">
                Session about to expire
              </h2>
            </FlexRow>

            <p className="text-[13px] text-[--Dark-Grey] m-0 leading-[18px]">
              You've been inactive for a while. You will be signed out in
              <span className="text-[--Error] font-bold font-mono ml-[4px]">
                { formatMMSS(remaining) }
              </span>
              { remaining > 0 ? '.' : '…' }
            </p>

            { retry &&
              <div className="bg-[#FDECEA] border border-[--Error] rounded-[4px] p-[8px] flex items-start gap-[6px]">
                <span className="material-icons !text-[--Error] text-[16px]">error</span>
                <span className="text-[12px] text-[--Error] leading-[16px]">
                  Couldn't reach the server. Try again, or sign out.
                </span>
              </div>
            }

            <FlexRow flex={0} justify="end" className="gap-[12px] mt-[4px]">
              <Button caption="Log out now"
                      layout="white"
                      onClick={handleLogout} />
              <Button caption={staying ? "Staying…" : (retry ? "Retry" : "Stay logged in")}
                      isDisabled={staying}
                      onClick={handleStay} />
            </FlexRow>
          </FlexRow>
        </ModalBody>
      </ModalContent>
    </MModal>
  );
};

export default SessionExpiringModal;
